import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/components/ui/Tooltip';
import { parseApiUtc } from '@/utils/apiDate';
import { EquipmentPriority } from '../../api/General/businessRoles';

/**
 * One place for the chrome every equipment requirement wears.
 *
 * Before this, the priority tint map was copy-pasted verbatim into five files, each carrying a
 * comment promising they "read as one system". Five copies kept in sync by comments is how they
 * drift; the waived and lifted states ship here so they cannot repeat that.
 */

// Tints are 10%, not 5%: at 5% the fill read as white and the chip was carried by its border alone.
// 10% is also the ceiling here, since 15% drops the Important chip to 4.48:1 and fails WCAG AA.
export const priorityToneClass: Record<EquipmentPriority, string> = {
  [EquipmentPriority.Essential]: 'border-destructive/40 text-destructive-strong bg-destructive/10',
  [EquipmentPriority.Important]: 'border-warning/40 text-warning-strong bg-warning/10',
  [EquipmentPriority.Helpful]: 'border-primary/40 text-primary bg-primary/10',
  [EquipmentPriority.Optional]: 'border-border text-muted-foreground-strong bg-muted',
};

export const priorityLabelKey: Record<EquipmentPriority, string> = {
  [EquipmentPriority.Essential]: 'businessRoles.priority.essential',
  [EquipmentPriority.Important]: 'businessRoles.priority.important',
  [EquipmentPriority.Helpful]: 'businessRoles.priority.helpful',
  [EquipmentPriority.Optional]: 'businessRoles.priority.optional',
};

export const priorityOrder: EquipmentPriority[] = [
  EquipmentPriority.Essential,
  EquipmentPriority.Important,
  EquipmentPriority.Helpful,
  EquipmentPriority.Optional,
];

export type RequirementSource = 'role' | 'goal' | 'added';

const sourceToneClass: Record<RequirementSource, string> = {
  role: 'border-blue-200 text-blue-700 bg-blue-50',
  goal: 'border-emerald-200 text-emerald-700 bg-emerald-50',
  added: 'border-violet-200 text-violet-700 bg-violet-50',
};

const sourceLabelKey: Record<RequirementSource, string> = {
  role: 'assets.coverage.source.roleBadge',
  goal: 'assets.coverage.source.goalBadge',
  added: 'assets.coverage.source.addedBadge',
};

// Where a requirement came from decides who can change it, so the chip explains itself.
const sourceTooltipKey: Record<RequirementSource, string> = {
  role: 'assets.coverage.source.roleBadgeTooltip',
  goal: 'assets.coverage.source.goalBadgeTooltip',
  added: 'assets.coverage.source.addedBadgeTooltip',
};

export const PriorityChip: React.FC<{ priority: EquipmentPriority }> = ({ priority }) => {
  const { t } = useTranslation();
  return (
    <Tooltip content={t('assets.coverage.priorityChipTooltip')}>
      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${priorityToneClass[priority]}`}>
        {t(priorityLabelKey[priority])}
      </span>
    </Tooltip>
  );
};

export const SourceChip: React.FC<{ source: RequirementSource }> = ({ source }) => {
  const { t } = useTranslation();
  return (
    <Tooltip content={t(sourceTooltipKey[source])}>
      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${sourceToneClass[source]}`}>
        {t(sourceLabelKey[source])}
      </span>
    </Tooltip>
  );
};

interface WaiverState {
  isWaived?: boolean;
  waivedByName?: string | null;
  waivedOn?: string | null;
  waiverNote?: string | null;
  waiverLiftedBySource?: string | null;
}

// Struck-through name for a waived requirement. Callers pass the label they would have rendered.
export const RequirementLabel: React.FC<{ label: string; state: WaiverState }> = ({ label, state }) => (
  <span className={state.isWaived ? 'line-through text-muted-foreground' : 'font-medium text-foreground'}>
    {label}
  </span>
);

// "Not counted" when excluded, "Counting again" when a waiver lifted. Renders nothing otherwise.
export const WaiverBadge: React.FC<{ state: WaiverState }> = ({ state }) => {
  const { t } = useTranslation();
  const waivedOn = parseApiUtc(state.waivedOn);
  const date = waivedOn ? waivedOn.toLocaleDateString() : '';

  if (state.isWaived) {
    return (
      <Tooltip content={t('assets.coverage.notCountedTooltip', { name: state.waivedByName || t('assets.coverage.someone'), date })}>
        <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground-strong">
          {t('assets.coverage.notCounted')}
        </span>
      </Tooltip>
    );
  }

  if (state.waiverLiftedBySource) {
    return (
      <Tooltip content={t('assets.coverage.countingAgainTooltip', {
        name: state.waivedByName || t('assets.coverage.someone'),
        date,
        source: state.waiverLiftedBySource,
      })}>
        <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-[11px] font-medium text-primary">
          {t('assets.coverage.countingAgain')}
        </span>
      </Tooltip>
    );
  }

  return null;
};
